import React from "react";
import {
  Microphone, Camera, MagnifyingGlass, Handshake, Plant, Basket,
  CheckCircle, Warning, Sparkle, MapTrifold, Sun, Package, Coins,
  Bug, Leaf, ChartLine, Envelope, PaperPlaneTilt, ShoppingBag,
} from "@phosphor-icons/react";

/**
 * PhoneMock — FarmersPulse-branded phone screenshot mockup.
 *
 * Deliberately visually DIFFERENT from ChatGPT-style mocks:
 *  - Warm cream (#F5EBDC) shell chrome, not clinical white
 *  - Terracotta (#B85F2A) accents in place of sage-green
 *  - Serif-display headings + mono-tech eyebrows (matches Shell)
 *  - No app bottom-bar, no "Work with ChatGPT" pill
 *  - Structured content rendered from data, so 12 tools ship without
 *    hand-writing 24 mockup components.
 *
 * Content shape (see /data/farmerToolkit.js):
 *   { eyebrow, title, subtitle, badge, blocks: [...] }
 *
 * Block kinds:
 *   { kind: "input",    label, value, icon? }
 *   { kind: "textarea", label, value }
 *   { kind: "chips",    label, options, active? }
 *   { kind: "stat-row", items: [{ label, value }, ...] }
 *   { kind: "list",     title?, items: [{ icon?, title, sub? }] }
 *   { kind: "cta",      label }
 *   { kind: "info",     text, tone? ("warn" | "ok") }
 *   { kind: "gallery",  n }
 *   { kind: "kv-grid",  items: [{ label, value }, ...] }
 */

const ICONS = {
  microphone: Microphone, camera: Camera, magnify: MagnifyingGlass,
  handshake: Handshake, plant: Plant, basket: Basket, check: CheckCircle,
  warning: Warning, sparkle: Sparkle, map: MapTrifold, sun: Sun,
  package: Package, coins: Coins, bug: Bug, leaf: Leaf, chart: ChartLine,
  envelope: Envelope, send: PaperPlaneTilt, shop: ShoppingBag,
};

function IconFor({ name, size = 14 }) {
  const I = ICONS[name] || Sparkle;
  return <I size={size} weight="duotone" className="text-[color:var(--np-brand,#B85F2A)]" />;
}

function Chip({ children, active, warn }) {
  const cls = active
    ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent"
    : warn
      ? "bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] border-[color:var(--np-brand,#B85F2A)]/25"
      : "bg-white text-[#3A3830] border-black/10";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase ${cls}`}>
      {children}
    </span>
  );
}

function Block({ b }) {
  switch (b.kind) {
    case "input":
      return (
        <div className="rounded-xl bg-white border border-black/5 p-2.5">
          <div className="font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#8A857A]">{b.label}</div>
          <div className="mt-0.5 flex items-center gap-2 text-[13px] text-[#1F1D1A]">
            {b.icon && <IconFor name={b.icon} size={12} />}
            <span className="truncate">{b.value}</span>
          </div>
        </div>
      );
    case "textarea":
      return (
        <div className="rounded-xl bg-white border border-black/5 p-2.5">
          <div className="font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#8A857A]">{b.label}</div>
          <div className="mt-1 text-[12px] leading-snug text-[#1F1D1A]">{b.value}</div>
        </div>
      );
    case "chips":
      return (
        <div>
          {b.label && <div className="font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#8A857A] mb-1">{b.label}</div>}
          <div className="flex flex-wrap gap-1.5">
            {b.options.map((o) => <Chip key={o} active={o === b.active}>{o}</Chip>)}
          </div>
        </div>
      );
    case "stat-row":
      return (
        <div className="grid grid-cols-2 gap-2">
          {b.items.map((it) => (
            <div key={it.label} className="rounded-xl bg-white border border-black/5 p-2.5">
              <div className="font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#8A857A]">{it.label}</div>
              <div className="mt-0.5 font-serif-display text-[16px] leading-none text-[#1F1D1A]">{it.value}</div>
            </div>
          ))}
        </div>
      );
    case "kv-grid":
      return (
        <div className="rounded-xl bg-white border border-black/5 divide-y divide-black/5">
          {b.items.map((it) => (
            <div key={it.label} className="flex items-center justify-between px-2.5 py-1.5">
              <span className="font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#8A857A]">{it.label}</span>
              <span className="text-[12px] text-[#1F1D1A]">{it.value}</span>
            </div>
          ))}
        </div>
      );
    case "list":
      return (
        <div className="rounded-xl bg-white border border-black/5 p-2.5">
          {b.title && <div className="font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#8A857A] mb-1.5">{b.title}</div>}
          <ul className="space-y-1.5">
            {b.items.map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] text-[#1F1D1A]">
                {it.icon
                  ? <span className="mt-0.5"><IconFor name={it.icon} size={12} /></span>
                  : <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] shrink-0" />}
                <div className="min-w-0">
                  <div className="leading-tight">{it.title}</div>
                  {it.sub && <div className="text-[10.5px] text-[#6C685E] leading-tight">{it.sub}</div>}
                </div>
                {typeof it.right !== "undefined" && (
                  <span className="ml-auto font-mono-tech text-[10px] text-[color:var(--np-brand,#B85F2A)]">{it.right}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    case "cta":
      return (
        <div className="rounded-xl bg-[#151312] text-[#F5EBDC] p-2.5 flex items-center justify-center gap-1.5 font-mono-tech text-[10px] tracking-[0.22em] uppercase">
          {b.icon && <IconFor name={b.icon} size={12} />}
          {b.label}
        </div>
      );
    case "info":
      return (
        <div className={`rounded-xl border p-2 flex items-start gap-1.5 text-[11px] leading-snug ${
          b.tone === "ok"
            ? "bg-[#E8DFD0] border-black/10 text-[#3A3830]"
            : "bg-[color:var(--np-brand,#B85F2A)]/8 border-[color:var(--np-brand,#B85F2A)]/25 text-[color:var(--np-brand,#B85F2A)]"}`}
        >
          <Sparkle size={11} weight="fill" className="mt-0.5 shrink-0" />
          <span>{b.text}</span>
        </div>
      );
    case "gallery":
      return (
        <div className="grid grid-cols-3 gap-1.5">
          {Array.from({ length: b.n || 3 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-lg bg-[#E8DFD0] border border-dashed border-[#B85F2A]/30 flex items-center justify-center">
              <Camera size={14} weight="duotone" className="text-[color:var(--np-brand,#B85F2A)]/60" />
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

/**
 * PhoneMock — outer chrome + slotted content.
 * @param {object} data — { eyebrow, title, subtitle, badge, blocks[] }
 * @param {string} scale — "sm" | "md" | "lg" — affects width
 */
export default function PhoneMock({ data, scale = "md", label }) {
  const w = { sm: "w-[220px]", md: "w-[280px]", lg: "w-[320px]" }[scale] || "w-[280px]";
  return (
    <figure className={`${w} shrink-0`}>
      <div className="relative rounded-[36px] p-2 bg-[#151312] shadow-[0_24px_48px_-24px_rgba(0,0,0,0.5)]">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 h-4 w-24 rounded-b-2xl bg-[#151312] z-10" />
        <div className="rounded-[28px] overflow-hidden bg-[#F5EBDC]">
          {/* Status bar */}
          <div className="flex items-center justify-between px-4 pt-2 pb-1 text-[9px] font-mono-tech tracking-widest text-[#3A3830]">
            <span>9:41</span>
            <span className="flex items-center gap-1">
              <span className="h-1 w-1 rounded-full bg-[color:var(--np-brand,#B85F2A)]" />
              FARMERSPULSE
            </span>
          </div>
          {/* Content */}
          <div className="px-3 pt-1 pb-4 space-y-2.5">
            <header>
              <div className="flex items-center justify-between">
                <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  {data.eyebrow}
                </div>
                {data.badge && <Chip warn>{data.badge}</Chip>}
              </div>
              <h3 className="mt-1 font-serif-display text-[16px] leading-[1.1] text-[#1F1D1A]">{data.title}</h3>
              {data.subtitle && <p className="mt-0.5 text-[11px] leading-snug text-[#6C685E]">{data.subtitle}</p>}
            </header>
            {data.blocks.map((b, i) => <Block key={i} b={b} />)}
          </div>
        </div>
      </div>
      {label && (
        <figcaption className="mt-3 text-center font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#6C685E]">
          {label}
        </figcaption>
      )}
    </figure>
  );
}
